import { Typography } from '@mui/material';
import { animated, useSpring } from '@react-spring/web';
import useMountEffect from '@shared/hooks/useMountEffect';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const timeFormatter = (time: number) =>
  [Math.floor(time / 60), Math.ceil(time % 60)].join(':').replace(/:(\d)$/, ':0$1');

const initializeCooldown = (duration: number) => new Date(new Date().getTime() + duration * 1000);

type Props = {
  running: number;
  duration?: number;
  messageIntlKey: string;
  children: ReactNode;
};
const Cooldown: FC<Props> = ({ duration = 60, running, messageIntlKey, children }) => {
  const [countdown, control] = useSpring(() => ({ from: { value: 0 } }));
  const [cooldown, setCooldown] = useState<Date | null>(() =>
    running ? initializeCooldown(duration) : null,
  );
  const cooldownRef = useRef(cooldown);
  cooldownRef.current = cooldown;

  // all hooks use custom dependencies for efficiency
  /* eslint-disable react-hooks/exhaustive-deps */
  const timeValues = useMemo(() => {
    return { time: <animated.span>{countdown.value.to(timeFormatter)}</animated.span> };
  }, []);
  const startCooldown = useCallback(() => {
    const cooldown = cooldownRef.current;
    if (!cooldown) {
      return;
    }
    const diff = cooldown.getTime() - new Date().getTime();
    if (diff <= 0) {
      return;
    }
    control.set({ value: diff / 1000 });
    control.start({
      to: { value: 0 },
      onRest: () => setCooldown(null),
      config: { duration: diff },
    });
  }, []);
  useMountEffect(() => {
    const listener = () => {
      if (document.visibilityState === 'visible') {
        startCooldown();
      }
    };
    document.addEventListener('visibilitychange', listener);
    return () => document.removeEventListener('visibilitychange', listener);
  });
  useEffect(startCooldown, [cooldown]);
  useEffect(() => {
    if (running > 0) {
      setCooldown(initializeCooldown(duration));
    } else {
      setCooldown(null);
    }
  }, [running]);

  return cooldown ? (
    <Typography variant="body3" align="center" component="div">
      <FormattedMessage id={messageIntlKey} values={timeValues} />
    </Typography>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  );
};

export default Cooldown;
